import type React from 'react';
import { useState, useEffect, useRef, useMemo, useId } from 'react';
import styles from './document-info-popover.module.css';
import { Flex, Text, Tag, Avatar, Icon, useReducedMotion, Input, iconWeight, Button } from '@nula/core-ui';
import { motion, AnimatePresence } from 'motion/react';
import { useAuthStore } from '@/stores/use-auth-store';
import { useWorkspaceDoc } from '@/lib/sync/hooks/use-workspace-doc';
import { useWorkspaceCollections } from '@/lib/sync/hooks/use-workspace-collections';
import { useLinkedCalendarEvents, useCalendarStore } from '@/stores/use-calendar-store';
import { getCalendarColor } from '@/lib/calendar/calendar-colors';
import { formatEventTime } from '@/lib/calendar/format-event-time';
import { formatRelativeDate } from '@/lib/sync/format-relative-date';
import { useNavigateToWorkspaceView } from '@/hooks/navigation';
import { useAccess } from '@/lib/sharing/hooks/use-access';
import { deriveOwner } from '@/lib/sharing/utils/derive-owner';
import { useCanEdit } from '@/lib/sharing/hooks/use-can-edit';
import { getEntityKindMeta } from '@/utils/entity-kind-meta';


interface DocumentInfoPopoverProps {
  entityId: string;
  open: boolean;
  onClose: () => void;
  tags: readonly string[];
  onAddTag?: (tag: string) => void;
  onRemoveTag?: (tag: string) => void;
  onAddCollection?: (collectionId: string) => void;
  onRemoveCollection?: (collectionId: string) => void;
}

export function DocumentInfoPopover({ entityId, open, onClose, tags, onAddTag, onRemoveTag, onAddCollection, onRemoveCollection }: DocumentInfoPopoverProps) {
  const panelRef = useRef<HTMLDivElement>(null);
  const titleId = useId();
  const shouldReduce = useReducedMotion();
  const fallbackUser = useAuthStore((s) => s.user);
  const entityMeta = useWorkspaceDoc(entityId);
  const access = useAccess(entityId);
  const canEdit = useCanEdit(entityId);
  const linkedEvents = useLinkedCalendarEvents(entityId);
  const subscriptions = useCalendarStore((s) => s.subscriptions);
  const selectEvent = useCalendarStore((s) => s.selectEvent);
  const navigateToView = useNavigateToWorkspaceView();
  const [wordCount, setWordCount] = useState(0);
  const [charCount, setCharCount] = useState(0);
  const [addingTag, setAddingTag] = useState(false);
  const [tagDraft, setTagDraft] = useState('');
  const [pickingCollection, setPickingCollection] = useState(false);
  const [collectionSearch, setCollectionSearch] = useState('');

  // Resolve collection IDs to names from the projection
  const collections = useWorkspaceCollections();
  const collectionNames = useMemo(() => {
    if (!entityMeta?.collectionIds?.length) return [];
    return entityMeta.collectionIds
      .map((id) => collections.find((c) => c.id === id)?.name)
      .filter((name): name is string => !!name);
  }, [entityMeta?.collectionIds, collections]);

  // Derive the owner from the entity store access info.
  //
  // When the current user is the owner (or the resource is not in the store
  // yet), fall back to the logged-in user.
  //
  // For non-owner viewers, use access.grantedBy — the server populates name,
  // email, and avatar from the granter's user row.
  //
  // Caveat: if Alice grants Carol admin and Carol grants Bob edit, Bob sees
  // Carol as owner instead of Alice. Accurate chain-owner display requires a
  // dedicated GET /documents/{id}/owner endpoint (filed as follow-up).
  const owner = useMemo(() => deriveOwner(access, fallbackUser), [access, fallbackUser]);

  // Compute word/char count from editor DOM when open
  useEffect(() => {
    if (!open) return;
    const editorEl = document.querySelector('[contenteditable="true"]');
    const text = editorEl?.textContent ?? '';
    setCharCount(text.length);
    setWordCount(text.trim().split(/\s+/).filter(Boolean).length);
  }, [open]);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (panelRef.current?.contains(e.target as Node)) return;
      onClose();
    };
    // Delay to avoid catching the click that opened this
    const timer = setTimeout(() => document.addEventListener('mousedown', handler), 50);
    return () => { clearTimeout(timer); document.removeEventListener('mousedown', handler); };
  }, [open, onClose]);

  // Close on Escape
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [open, onClose]);

  // Move focus into dialog when it opens
  useEffect(() => {
    if (!open) return;
    const first = panelRef.current?.querySelector<HTMLElement>('button, [href], input, [tabindex]');
    first?.focus();
  }, [open]);

  const readMin = Math.max(1, Math.round(wordCount / 200));

  const commitTag = () => {
    const trimmed = tagDraft.trim().replace(/^#/, '');
    if (trimmed) onAddTag?.(trimmed);
    setTagDraft('');
    setAddingTag(false);
  };

  const content = (
    <Flex vertical>
      {/* Owner */}
      <Flex vertical className={styles.section}>
        <Text id={titleId} size="xs" strong className={styles.sectionTitle}>
          Owner
        </Text>
        <Flex align="center" gap="sm">
          <Avatar size="sm" src={owner?.avatarUrl} className={styles.avatar}>
            {(owner?.name || 'U').charAt(0).toUpperCase()}
          </Avatar>
          <Flex vertical>
            <Text size="sm" strong>{owner?.name || 'Unknown'}</Text>
            {owner?.email && (
              <Text size="xs" type="secondary">{owner.email}</Text>
            )}
          </Flex>
        </Flex>
      </Flex>

      {/* Statistics */}
      <Flex vertical className={styles.sectionBordered}>
        <Text size="xs" strong className={styles.sectionTitle}>
          Statistics
        </Text>
        <Flex justify="space-between" className={styles.metaRow}>
          <Text size="xs" type="secondary">Words</Text>
          <Text size="xs" strong className={styles.tabularNums}>{wordCount.toLocaleString()}</Text>
        </Flex>
        <Flex justify="space-between" className={styles.metaRow}>
          <Text size="xs" type="secondary">Characters</Text>
          <Text size="xs" strong className={styles.tabularNums}>{charCount.toLocaleString()}</Text>
        </Flex>
        <Flex justify="space-between">
          <Text size="xs" type="secondary">Read time</Text>
          <Text size="xs" strong className={styles.tabularNums}>{readMin} min</Text>
        </Flex>
      </Flex>

      {/* Collections */}
      <Flex vertical className={styles.sectionBordered}>
        <Text size="xs" strong className={styles.sectionTitle}>
          Collections
        </Text>
        <Flex wrap gap="xs" align="center">
          {collectionNames.map((name, i) => {
            const id = entityMeta?.collectionIds?.[i] ?? '';
            return (
              <Tag
                key={id}
                bordered={false}
                closable={canEdit}
                onClose={canEdit ? () => onRemoveCollection?.(id) : undefined}
                className={styles.tagStyle}
              >
                {name}
              </Tag>
            );
          })}
          {canEdit && (pickingCollection ? (
            <Input
              autoFocus
              size="sm"
              variant="plain"
              aria-label="Search collections"
              placeholder="Search collections…"
              value={collectionSearch}
              onChange={(e) => setCollectionSearch(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Escape') { setPickingCollection(false); setCollectionSearch(''); }
              }}
              className={styles.collectionSearchInput}
            />
          ) : (
            <Button
              type="button"
              variant="text"
              size="sm"
              aria-label="Add collection"
              onClick={() => setPickingCollection(true)}
              className={styles.addTagButton}
            >
              + Add
            </Button>
          ))}
        </Flex>
        {pickingCollection && canEdit && (
          <Flex vertical className={styles.collectionResults}>
            {collections
              .filter((c) => !entityMeta?.collectionIds?.includes(c.id))
              .filter((c) => c.name.toLowerCase().includes(collectionSearch.toLowerCase()))
              .map((c) => (
                <button
                  key={c.id}
                  type="button"
                  className={styles.collectionOption}
                  onClick={() => { onAddCollection?.(c.id); setPickingCollection(false); setCollectionSearch(''); }}
                >
                  {c.name}
                </button>
              ))}
          </Flex>
        )}
      </Flex>

      {/* Tags */}
      <Flex vertical className={styles.sectionBordered}>
        <Text size="xs" strong className={styles.sectionTitle}>
          Tags
        </Text>
        <Flex wrap gap="xs" align="center">
          {tags.map((tag) => (
            <Tag
              key={tag}
              bordered={false}
              closable={canEdit}
              onClose={canEdit ? () => onRemoveTag?.(tag) : undefined}
              className={styles.tagStyle}
            >
              {tag}
            </Tag>
          ))}
          {canEdit && (addingTag ? (
            <Input
              autoFocus
              size="sm"
              variant="plain"
              name="tag"
              aria-label="Tag name"
              placeholder="tag name…"
              value={tagDraft}
              onChange={(e) => setTagDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') commitTag();
                if (e.key === 'Escape') { setTagDraft(''); setAddingTag(false); }
              }}
              onBlur={commitTag}
              className={styles.addTagInput}
            />
          ) : (
            <Button
              type="button"
              variant="text"
              size="sm"
              aria-label="Add tag"
              onClick={() => setAddingTag(true)}
              className={styles.addTagButton}
            >
              + Add
            </Button>
          ))}
        </Flex>
      </Flex>

      {/* Calendar Events */}
      {linkedEvents.length > 0 && (
        <Flex vertical className={styles.sectionBordered}>
          <Text size="xs" strong className={styles.sectionTitle}>
            Calendar Events
          </Text>
          <Flex vertical gap="xs">
            {linkedEvents.map((event) => {
              const color = getCalendarColor(event.subscriptionId, subscriptions[event.subscriptionId]?.color);
              const dateLabel = formatEventTime(event);
              return (
                <Flex
                  key={event.id}
                  align="center"
                  gap="xs"
                  onClick={() => { selectEvent(event); navigateToView('calendar'); onClose(); }}
                  className={styles.calendarEventRow}
                >
                  {/* Color dot: runtime value from subscription color → CSS custom property */}
                  <Flex
                    className={styles.calendarColorDot}
                    style={{ '--cal-color': color } as React.CSSProperties}
                  />
                  <Flex vertical className={styles.calendarEventContent}>
                    <Text size="xs" strong className={styles.calendarEventTitle}>
                      {event.title}
                    </Text>
                    <Text size="xs" type="secondary">{dateLabel}</Text>
                  </Flex>
                  <Icon name="open_in_new" size={12} weight={iconWeight.light} className={styles.calendarIcon} />
                </Flex>
              );
            })}
          </Flex>
        </Flex>
      )}

      {/* Document */}
      <Flex vertical className={styles.sectionBordered}>
        <Text size="xs" strong className={styles.sectionTitle}>
          Document
        </Text>
        <Flex justify="space-between" className={styles.metaRow}>
          <Text size="xs" type="secondary">Type</Text>
          <Text size="xs" strong>{getEntityKindMeta(entityMeta?.kind).label}</Text>
        </Flex>
        <Flex justify="space-between" className={styles.metaRow}>
          <Text size="xs" type="secondary">Last edited</Text>
          <Text size="xs" strong>{formatRelativeDate(entityMeta?.updatedAt ?? '')}</Text>
        </Flex>
      </Flex>
    </Flex>
  );

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          ref={panelRef}
          role="dialog"
          aria-labelledby={titleId}
          aria-modal="true"
          initial={shouldReduce ? { opacity: 0 } : { opacity: 0, y: 4 }}
          animate={shouldReduce ? { opacity: 1 } : { opacity: 1, y: 0 }}
          exit={shouldReduce ? { opacity: 0 } : { opacity: 0, y: 4 }}
          transition={{ duration: 0.15 }}
          onMouseDown={(e) => e.stopPropagation()}
          onClick={(e) => e.stopPropagation()}
          className={styles.panel}
        >
          {content}
        </motion.div>
      )}
    </AnimatePresence>
  );
}
